import React from 'react';
import {
  CreditCard,
  Truck,
  Users,
  BarChart3,
  Search,
  Download,
  Plus,
  User,
  Edit,
  Eye,
  Printer,
  MoreVertical,
  Info
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from 'recharts';

const PaymentsTab = ({
  financialData,
  formatCurrency,
  paymentFilters,
  setPaymentFilters,
  setShowAddPaymentModal,
  filteredPayments,
  handleViewPayment,
  handleEditPayment,
  handleQuickStatusUpdate,
  toggleActionMenu,
  openActionMenu,
  setOpenActionMenu,
  handlePrintPaystub,
  getPaymentFinancials,
  formatDateDisplay,
  toTitleCase,
  paymentsLoading = false,
  paymentsError = null,
  pendingPayments = { payments: [], totalPending: 0, count: 0 },
  pagination = { page: 1, limit: filteredPayments.length || 0, totalCount: filteredPayments.length || 0, totalPages: 1, hasNext: false, hasPrev: false },
  onRefreshPayments,
  onPageChange
}) => {
  const paymentSummary = financialData.paymentSummary || {};
  const thisWeekSummary = paymentSummary.thisWeek || {};
  const thisMonthSummary = paymentSummary.thisMonth || {};
  const yearToDateSummary = paymentSummary.yearToDate || {};
  const pendingAmount = typeof thisWeekSummary.pendingPayments === 'number'
    ? thisWeekSummary.pendingPayments
    : Number(pendingPayments.totalPending) || 0;
  const driverPayments = thisMonthSummary.driverPayments || 0;
  const dispatcherPayments = thisMonthSummary.dispatcherPayments || 0;
  const avgDriverPay = thisMonthSummary.avgDriverPay || (pendingPayments?.driverAverage || 0);
  const avgDispatcherPay = thisMonthSummary.avgDispatcherPay || 0;
  const safePagination = pagination || { page: 1, limit: filteredPayments.length || 0, totalCount: filteredPayments.length || 0, totalPages: 1, hasNext: false, hasPrev: false };
  const hasPayments = filteredPayments.length > 0;
  const showEmptyState = !paymentsLoading && !hasPayments;
  const [showYtdInfo, setShowYtdInfo] = React.useState(false);

  const handleRefreshClick = () => {
    if (typeof onRefreshPayments === 'function') {
      onRefreshPayments();
    }
  };

  const handlePageNav = (direction) => {
    if (typeof onPageChange !== 'function') {
      return;
    }
    const nextPage = safePagination.page + direction;
    if (nextPage < 1) {
      return;
    }
    onPageChange(nextPage);
  };

  const renderPagination = () => {
    if (!hasPayments) {
      return null;
    }
    return (
      <div className="flex flex-col md:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 text-sm text-gray-600 mt-6">
        <div>
          Page {safePagination.page} of {safePagination.totalPages || 1}
          {safePagination.totalCount ? ` • ${safePagination.totalCount} payments` : ''}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handlePageNav(-1)}
            disabled={!safePagination.hasPrev || paymentsLoading}
            className={`px-3 py-1.5 rounded-lg border ${
              !safePagination.hasPrev || paymentsLoading
                ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                : 'text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Previous
          </button>
          <button
            onClick={() => handlePageNav(1)}
            disabled={!safePagination.hasNext || paymentsLoading}
            className={`px-3 py-1.5 rounded-lg border ${
              !safePagination.hasNext || paymentsLoading
                ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                : 'text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
  <div>
    {/* Payment Summary Cards */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Total Paid This Week</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(thisWeekSummary.totalPaid || 0)}</p>
          </div>
          <CreditCard className="w-8 h-8 text-green-500" />
        </div>
        <div className="mt-2 text-sm text-gray-500">Pending: {formatCurrency(pendingAmount)}</div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Driver Payments</p>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(driverPayments)}</p>
          </div>
          <Truck className="w-8 h-8 text-blue-500" />
        </div>
        <div className="mt-2 text-sm text-gray-500">Avg: {formatCurrency(avgDriverPay || 0)}</div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Dispatcher Payments</p>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(dispatcherPayments)}</p>
          </div>
          <Users className="w-8 h-8 text-purple-500" />
        </div>
        <div className="mt-2 text-sm text-gray-500">Avg: {formatCurrency(avgDispatcherPay || 0)}</div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 relative">
        <div className="flex items-center justify-between">
          <div>
            <div className="relative flex items-center gap-1 text-sm text-gray-600">
              <span>YTD Total</span>
              <button
                type="button"
                onClick={() => setShowYtdInfo((prev) => !prev)}
                className="text-indigo-500 hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 rounded-full"
                aria-expanded={showYtdInfo}
                aria-controls="ytd-info-panel"
              >
                <Info className="w-4 h-4" />
                <span className="sr-only">Learn how YTD Total is calculated</span>
              </button>
              {showYtdInfo && (
                <div
                  id="ytd-info-panel"
                  className="absolute z-20 top-full left-0 mt-2 w-64 bg-white border border-indigo-100 rounded-lg shadow-xl p-3 text-xs text-gray-600"
                >
                  YTD Total only includes payments whose status is marked as paid. Pending or processing payments appear once completed.
                </div>
              )}
            </div>
            <p className="text-2xl font-bold text-indigo-600">{formatCurrency(yearToDateSummary.totalPaid || 0)}</p>
          </div>
          <BarChart3 className="w-8 h-8 text-indigo-500" />
        </div>
        <div className="mt-2 text-sm text-gray-500">Bonuses: {formatCurrency(yearToDateSummary.totalBonuses || 0)}</div>
      </div>
    </div>

    {/* Filters and Search */}
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center space-x-2">
          <Search className="w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search by name or payment ID..."
            value={paymentFilters.search}
            onChange={(e) => setPaymentFilters({...paymentFilters, search: e.target.value})}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-64"
          />
        </div>

        <select 
          value={paymentFilters.recipientType}
          onChange={(e) => setPaymentFilters({...paymentFilters, recipientType: e.target.value})}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Types</option>
          <option value="driver">Drivers</option>
          <option value="dispatcher">Dispatchers</option>
          <option value="admin">Admin Staff</option>
        </select>

        <select 
          value={paymentFilters.status}
          onChange={(e) => setPaymentFilters({...paymentFilters, status: e.target.value})}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="failed">Failed</option>
        </select>

        <select 
          value={paymentFilters.paymentMethod}
          onChange={(e) => setPaymentFilters({...paymentFilters, paymentMethod: e.target.value})}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Methods</option>
          <option value="direct_deposit">Direct Deposit</option>
          <option value="check">Check</option>
          <option value="cash">Cash</option>
          <option value="wire">Wire Transfer</option>
        </select>

        <select 
          value={paymentFilters.calculationType || 'all'}
          onChange={(e) => setPaymentFilters({...paymentFilters, calculationType: e.target.value})}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Calculations</option>
          <option value="percentage">Percentage</option>
          <option value="flat_rate">Flat Rate</option>
          <option value="mileage">Mileage</option>
          <option value="owner_operator">Owner Operator</option>
          <option value="legacy">Legacy</option>
        </select>

        <div className="flex items-center space-x-2">
          <input
            type="date"
            value={paymentFilters.startDate}
            onChange={(e) => setPaymentFilters({...paymentFilters, startDate: e.target.value})}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={paymentFilters.endDate}
            onChange={(e) => setPaymentFilters({...paymentFilters, endDate: e.target.value})}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          <Download className="w-4 h-4" />
          <span>Export</span>
        </button>

        <button 
          onClick={() => setShowAddPaymentModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add Payment</span>
        </button>
      </div>
    </div>

    {paymentsError && (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-6 flex flex-wrap items-center justify-between gap-3">
        <span>{paymentsError}</span>
        {onRefreshPayments && (
          <button
            onClick={handleRefreshClick}
            className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        )}
      </div>
    )}

    {/* Payment History Table */}
    <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
      <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
        {onRefreshPayments && (
          <div className="flex items-center gap-3 text-sm text-gray-500">
            {paymentsLoading && <span>Refreshing...</span>}
            <button
              onClick={handleRefreshClick}
              disabled={paymentsLoading}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border ${
                paymentsLoading
                  ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              Refresh
            </button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto hidden lg:block">
        {paymentsLoading && !hasPayments ? (
          <div className="py-10 text-center text-gray-500">Loading payments...</div>
        ) : showEmptyState ? (
          <div className="py-10 text-center text-gray-500">No payments match your filters.</div>
        ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment ID</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Calculation Type</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base Details</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Period</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Amount</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Amount</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredPayments.map((payment, index) => {
              const { totalDeductions, totalBonuses, calculatedGross, netPayment } = getPaymentFinancials(payment);
              const payPeriodStart = formatDateDisplay(payment.payPeriodStart);
              const payPeriodEnd = formatDateDisplay(payment.payPeriodEnd);
              const methodLabel = payment.paymentMethod
                ? toTitleCase(payment.paymentMethod.replace('_', ' '))
                : 'Direct Deposit';
              const paymentIdentifier = payment.paymentNumber || payment.id;

              return (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      type="button"
                      onClick={() => handleViewPayment(payment)}
                      className="text-blue-600 hover:text-blue-800 underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                    >
                      {paymentIdentifier}
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{payment.recipientName}</div>
                        <div className="text-sm text-gray-500">{toTitleCase(payment.recipientType)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {payment.paymentCalculationType ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          payment.paymentCalculationType === 'percentage' ? 'bg-blue-100 text-blue-800' :
                          payment.paymentCalculationType === 'flat_rate' ? 'bg-purple-100 text-purple-800' :
                          payment.paymentCalculationType === 'mileage' ? 'bg-green-100 text-green-800' :
                          payment.paymentCalculationType === 'owner_operator' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {payment.paymentCalculationType === 'percentage' ? 'Percentage' :
                           payment.paymentCalculationType === 'flat_rate' ? 'Flat Rate' :
                           payment.paymentCalculationType === 'mileage' ? 'Mileage' :
                           payment.paymentCalculationType === 'owner_operator' ? 'Owner Op' :
                           'Legacy'}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600 capitalize">{payment.paymentType?.replace('_', ' ') || 'Legacy'}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    {payment.paymentCalculationType === 'percentage' && (
                      <div>
                        <div className="font-medium">{formatCurrency(payment.loadAmount || 0)}</div>
                        <div className="text-xs text-gray-500">{payment.percentageRate || 0}% rate</div>
                      </div>
                    )}
                    {payment.paymentCalculationType === 'flat_rate' && (
                      <div>
                        <div className="font-medium">{formatCurrency(payment.salaryAmount || 0)}</div>
                        <div className="text-xs text-gray-500">Fixed salary</div>
                      </div>
                    )}
                    {payment.paymentCalculationType === 'mileage' && (
                      <div>
                        <div className="font-medium">{(payment.totalMiles || 0).toLocaleString()} mi</div>
                        <div className="text-xs text-gray-500">${payment.mileageRate || 0}/mile</div>
                      </div>
                    )}
                    {payment.paymentCalculationType === 'owner_operator' && (
                      <div>
                        <div className="font-medium">{formatCurrency(payment.loadAmount || 0)}</div>
                        <div className="text-xs text-red-500">-{payment.commissionRate || 0}% comm.</div>
                      </div>
                    )}
                    {!payment.paymentCalculationType && (
                      <div>
                        <div className="font-medium">{payment.loadCount || 0} loads</div>
                        <div className="text-xs text-gray-500">{(payment.totalMiles || 0).toLocaleString()} miles</div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    <div>
                      <div className="font-medium">{payPeriodStart}</div>
                      <div className="text-xs text-gray-500">to {payPeriodEnd}</div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <div>
                      <div className="font-semibold text-blue-600">{formatCurrency(calculatedGross)}</div>
                      {totalDeductions > 0 && (
                        <div className="text-xs text-red-500">-{formatCurrency(totalDeductions)} ded.</div>
                      )}
                      {totalBonuses > 0 && (
                        <div className="text-xs text-green-500">+{formatCurrency(totalBonuses)} bonus</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                    {formatCurrency(netPayment)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      payment.status === 'paid' ? 'bg-green-100 text-green-800' :
                      payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      payment.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="relative inline-flex" data-payment-action-menu>
                      <button
                        type="button"
                        onClick={() => toggleActionMenu(payment.id)}
                        className="p-2 rounded-full border border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                        aria-haspopup="true"
                        aria-expanded={openActionMenu === payment.id}
                      >
                        <MoreVertical className="w-4 h-4" />
                        <span className="sr-only">Open actions</span>
                      </button>
                      {openActionMenu === payment.id && (
                        <div className="absolute right-0 mt-2 w-48 rounded-xl border border-gray-100 bg-white shadow-2xl z-20 divide-y divide-gray-100">
                          <div className="py-1">
                            <button
                              onClick={() => { handleViewPayment(payment); setOpenActionMenu(null); }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Eye className="w-4 h-4 text-indigo-500" />
                              View Details
                            </button>
                            <button
                              onClick={() => { handleEditPayment(payment); setOpenActionMenu(null); }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Edit className="w-4 h-4 text-blue-500" />
                              Edit Payment
                            </button>
                            <button
                              onClick={() => { handlePrintPaystub(payment); setOpenActionMenu(null); }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Printer className="w-4 h-4 text-gray-500" />
                              Print Paystub
                            </button>
                          </div>
                          {(payment.status === 'pending' || payment.status === 'processing') && (
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  handleQuickStatusUpdate(payment.id, 'paid');
                                  setOpenActionMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                              >
                                {payment.status === 'pending' ? 'Approve Payment' : 'Mark as Complete'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden px-4 py-4 space-y-4">
        {paymentsLoading && !hasPayments && (
          <div className="py-10 text-center text-gray-500">Loading payments...</div>
        )}
        {showEmptyState && (
          <div className="py-10 text-center text-gray-500">No payments match your filters.</div>
        )}
        {filteredPayments.map((payment) => {
          const { totalDeductions, totalBonuses, calculatedGross, netPayment } = getPaymentFinancials(payment);
          const methodLabel = payment.paymentMethod
            ? toTitleCase(payment.paymentMethod.replace('_', ' '))
            : 'Direct Deposit';
          const paymentIdentifier = payment.paymentNumber || payment.id;

          return (
            <div key={payment.id} className="border border-gray-100 rounded-2xl shadow-sm">
              <div className="flex items-start justify-between p-4 border-b border-gray-100">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Payment ID</p>
                  <button
                    type="button"
                    onClick={() => handleViewPayment(payment)}
                    className="text-left text-lg font-semibold text-blue-600 hover:text-blue-800 underline underline-offset-2"
                  >
                    {paymentIdentifier}
                  </button>
                  <p className="text-sm text-gray-500">{formatDateDisplay(payment.paymentDate || payment.payPeriodEnd)}</p>
                </div>
                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                  payment.status === 'paid' ? 'bg-green-100 text-green-800' :
                  payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  payment.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                </span>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Recipient</p>
                  <p className="text-base font-semibold text-gray-900">{payment.recipientName}</p>
                  <p className="text-sm text-gray-500">{toTitleCase(payment.recipientType)}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                  <div>
                    <p className="text-xs text-gray-500">Calculation</p>
                    <p className="font-medium">{payment.paymentCalculationType ? toTitleCase(payment.paymentCalculationType) : 'Legacy'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pay Period</p>
                    <p className="font-medium">{formatDateDisplay(payment.payPeriodStart)} – {formatDateDisplay(payment.payPeriodEnd)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Method</p>
                    <p className="font-medium">{methodLabel}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Loads / Miles</p>
                    <p className="font-medium">{payment.loadCount || 0} loads · {(payment.totalMiles || 0).toLocaleString()} mi</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Gross</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(calculatedGross)}</span>
                  </div>
                  {totalDeductions > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Deductions</span>
                      <span>-{formatCurrency(totalDeductions)}</span>
                    </div>
                  )}
                  {totalBonuses > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Bonuses</span>
                      <span>+{formatCurrency(totalBonuses)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base font-bold text-green-600 border-t border-gray-200 pt-2">
                    <span>Net Pay</span>
                    <span>{formatCurrency(netPayment)}</span>
                  </div>
                </div>

                <div className="flex justify-end" data-payment-action-menu>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => toggleActionMenu(payment.id)}
                      className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-full hover:bg-gray-50"
                    >
                      <MoreVertical className="w-4 h-4" />
                      Actions
                    </button>
                    {openActionMenu === payment.id && (
                      <div className="absolute right-0 mt-2 w-48 rounded-xl border border-gray-100 bg-white shadow-2xl z-20 divide-y divide-gray-100">
                        <div className="py-1">
                          <button
                            onClick={() => { handleViewPayment(payment); setOpenActionMenu(null); }}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <Eye className="w-4 h-4 text-indigo-500" />
                            View Details
                          </button>
                          <button
                            onClick={() => { handleEditPayment(payment); setOpenActionMenu(null); }}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <Edit className="w-4 h-4 text-blue-500" />
                            Edit Payment
                          </button>
                          <button
                            onClick={() => { handlePrintPaystub(payment); setOpenActionMenu(null); }}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <Printer className="w-4 h-4 text-gray-500" />
                            Print Paystub
                          </button>
                        </div>
                        {(payment.status === 'pending' || payment.status === 'processing') && (
                          <div className="py-1">
                            <button
                              onClick={() => {
                                handleQuickStatusUpdate(payment.id, 'paid');
                                setOpenActionMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                            >
                              {payment.status === 'pending' ? 'Approve Payment' : 'Mark as Complete'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {renderPagination()}
  </div>
  );
};

export default PaymentsTab;
